import React from "react";
import { Button } from "@/components/ui/button";
import { 
  RotateCcw, Undo2, Redo2, RefreshCw
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * ActionControls - Reusable Reset, Undo, Redo component
 * 
 * Props:
 * - onReset: function - Called when reset is clicked
 * - onUndo: function - Called when undo is clicked
 * - onRedo: function - Called when redo is clicked
 * - canUndo: boolean - Whether undo is available
 * - canRedo: boolean - Whether redo is available
 * - resetLabel: string - Custom label for reset tooltip
 * - variant: "default" | "compact" | "icon-only"
 * - showReset: boolean - Whether to show reset button (default true)
 * - showUndo: boolean - Whether to show undo button (default true)
 * - showRedo: boolean - Whether to show redo button (default true)
 */
const ActionControls = ({ 
  onReset, 
  onUndo, 
  onRedo, 
  canUndo = false, 
  canRedo = false,
  resetLabel = "Reset to default",
  undoLabel = "Undo last action",
  redoLabel = "Redo last action",
  variant = "default",
  showReset = true,
  showUndo = true,
  showRedo = true,
  className = ""
}) => {
  
  const isCompact = variant === "compact" || variant === "icon-only";
  const showLabels = variant === "default";

  return (
    <TooltipProvider>
      <div className={`flex items-center gap-2 ${className}`} data-testid="action-controls">
        {/* Undo Button */}
        {showUndo && onUndo && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size={isCompact ? "icon" : "sm"}
                onClick={onUndo}
                disabled={!canUndo}
                className={`${!canUndo ? 'opacity-50' : 'hover:bg-blue-500/10 hover:border-blue-500/50 hover:text-blue-400'}`}
                data-testid="undo-btn"
              >
                <Undo2 className={`${isCompact ? 'w-4 h-4' : 'w-4 h-4 mr-1'}`} />
                {showLabels && "Undo"}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{undoLabel}</p>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Redo Button */}
        {showRedo && onRedo && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size={isCompact ? "icon" : "sm"}
                onClick={onRedo}
                disabled={!canRedo}
                className={`${!canRedo ? 'opacity-50' : 'hover:bg-green-500/10 hover:border-green-500/50 hover:text-green-400'}`}
                data-testid="redo-btn"
              >
                <Redo2 className={`${isCompact ? 'w-4 h-4' : 'w-4 h-4 mr-1'}`} />
                {showLabels && "Redo"}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{redoLabel}</p>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Reset Button */}
        {showReset && onReset && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size={isCompact ? "icon" : "sm"}
                onClick={onReset}
                className="hover:bg-red-500/10 hover:border-red-500/50 hover:text-red-400"
                data-testid="reset-btn"
              >
                <RotateCcw className={`${isCompact ? 'w-4 h-4' : 'w-4 h-4 mr-1'}`} />
                {showLabels && "Reset"}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{resetLabel}</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
};

/**
 * useUndoRedo - Custom hook for undo/redo functionality
 * 
 * Usage:
 * const { state, setState, undo, redo, reset, canUndo, canRedo } = useUndoRedo(initialState);
 */
export const useUndoRedo = (initialState) => {
  const [history, setHistory] = React.useState([initialState]);
  const [currentIndex, setCurrentIndex] = React.useState(0);

  const state = history[currentIndex];

  const setState = React.useCallback((newState) => {
    const newHistory = history.slice(0, currentIndex + 1);
    newHistory.push(typeof newState === 'function' ? newState(state) : newState);
    
    // Limit history to 50 items
    if (newHistory.length > 50) {
      newHistory.shift();
    }
    
    setHistory(newHistory);
    setCurrentIndex(newHistory.length - 1);
  }, [history, currentIndex, state]);

  const undo = React.useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  }, [currentIndex]);

  const redo = React.useCallback(() => {
    if (currentIndex < history.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  }, [currentIndex, history.length]);

  const reset = React.useCallback(() => {
    setHistory([initialState]);
    setCurrentIndex(0);
  }, [initialState]);

  const canUndo = currentIndex > 0;
  const canRedo = currentIndex < history.length - 1;

  return { state, setState, undo, redo, reset, canUndo, canRedo, history };
};

export default ActionControls;
